import { useContext } from "react";
import {
  Gift,
  RefreshCw,
  LayoutDashboard,
  UserCog,
  Settings,
  Building,
  BarChart3,
  Tractor,
  Clock,
  PieChart,
  LineChart,
  Megaphone,
  Radio,
  Image as ImageIcon,
  MapPin,
  Search,
  GitBranch,
  Filter,
  UserCheck,
  Timer,
  Upload,
  Flame,
  TrendingUp,
  SlidersHorizontal,
  Activity,
  Target,
  Shield,
  CheckSquare,
  Mail,
  FileText,
  MessageSquare,
  Send,
  Inbox,
  ClipboardList,
  Zap,
  Tag,
  Briefcase,
  Phone,
  PhoneCall,
  PhoneIncoming,
  ListOrdered,
  Database,
  Handshake,
  BadgeCheck,
  FileSignature,
  IndianRupee,
  CalendarClock,
  ListChecks,
  Copy,
  Shuffle,
  Server,
  Gauge,
  Award,
  Calculator,
  Wallet,
  Building2,
  Layers,
  Sparkles,
  Users,
  Sliders,
  GraduationCap,
  Siren,
  ShieldCheck,
  Telescope,
  MailCheck,
  Radar,
  HeartHandshake,
  Presentation,
  Leaf,
  Link2,
} from "lucide-react";
import { AuthContext } from "../context/AuthContext";

export const useNavigation = () => {
  const login_role = sessionStorage.getItem("login_role");
  const { hasPermission, hasFeature, isEngageTenant } = useContext(AuthContext);

  const isSuperAdmin = login_role === "super_admin";
  // admin + super_admin may administer permissions (roles & users).
  const isPermAdmin = isSuperAdmin || login_role === "admin";

  const can = (module) => hasFeature(module) && (isSuperAdmin || hasPermission(module, "view"));

  // Meta Ads pages: one plan feature ('meta_ads') covers the whole capability,
  // but each report carries its own permission key — the backend enforces them
  // individually, so a role can hold Ad Performance without Marketing ROI.
  const canAd = (key) => hasFeature("meta_ads")
    && (isSuperAdmin || hasPermission(key, "view"));

  // ── Engage (Hospital CRM) tenants get a distinct nav ─────────────────────
  if (isEngageTenant && isEngageTenant()) {
    const engageReportsSubItems = [
      // Timeline leads the list (asked for on 2026-10-13).
      { label: "Timeline",               href: "/engage/reports/timeline",           icon: Clock },
      // Unified dashboard first — every report on one filtered page.
      { label: "All Reports",            href: "/engage/reports",                    icon: LayoutDashboard },
      { label: "Engagement",             href: "/engage/reports/engagement",         icon: Activity },
      { label: "Follow-up Completion",   href: "/engage/reports/followup-completion", icon: ListChecks },
      { label: "No-Show Rate",           href: "/engage/reports/no-show-rate",       icon: Clock },
      { label: "Enquiry Funnel",         href: "/engage/reports/enquiry-funnel",     icon: PieChart },
      { label: "Conversion Analytics",   href: "/engage/reports/conversion",         icon: Gauge },
      { label: "Source ROI",             href: "/engage/reports/source-roi",         icon: IndianRupee },
      { label: "Referrer Leaderboard",   href: "/engage/reports/referrer-leaderboard", icon: TrendingUp },
      { label: "Meta Performance",       href: "/engage/reports/meta-performance",   icon: Award },
      { label: "Digital Marketing ROI",  href: "/engage/reports/marketing-roi",      icon: LineChart },
      { label: "Health Camp ROI",        href: "/engage/reports/camp-roi",           icon: Target },
      { label: "Corporate Renewals",     href: "/engage/reports/corporate-renewals", icon: CalendarClock },
    ];

    // Meta ad reports that work on enquiries. Their CRM half (_crm_outcomes)
    // counts Meta-attributed enquiries on an Engage tenant; Audience and
    // Geography are Meta data only. ROI, attribution, funnel, pacing and the rest
    // still query TrustEdge leads, so they stay off this menu.
    const canEngageAd = (key) => isSuperAdmin || hasPermission(key, "view");
    const engageAdReportsSubItems = [
      canEngageAd("report_ad_performance") && { label: "Ad Performance", href: "/reports/ad-performance", icon: Megaphone },
      canEngageAd("report_ad_dashboard")   && { label: "Live Ad Spend",  href: "/reports/ad-dashboard",   icon: Radio },
      canEngageAd("report_ad_creative")    && { label: "Ad Creative",    href: "/reports/ad-creative",    icon: ImageIcon },
      canEngageAd("report_ad_audience")    && { label: "Ad Audience",    href: "/reports/ad-audience",    icon: Users },
      canEngageAd("report_ad_geography")   && { label: "Ad Geography",   href: "/reports/ad-geography",   icon: MapPin },
    ].filter(Boolean);

    // Feature keys are the shared whatsapp_* ones; the permission is the single
    // 'whatsapp' key that every platform_core/whatsapp endpoint enforces.
    const canEngageWhatsApp = (feature) =>
      hasFeature(feature) && (isPermAdmin || hasPermission("whatsapp", "view"));

    const engageWhatsAppSubItems = [
      canEngageWhatsApp("whatsapp_campaigns")   && { label: "Campaigns",      href: "/whatsapp/campaigns",            icon: Send },
      canEngageWhatsApp("whatsapp_inbox")       && { label: "Inbox",          href: "/whatsapp/inbox",                icon: Inbox },
      canEngageWhatsApp("whatsapp_integration") && { label: "WA Integration", href: "/settings/whatsapp-integration", icon: MessageSquare },
      canEngageWhatsApp("whatsapp_templates")   && { label: "WA Templates",   href: "/settings/whatsapp-templates",   icon: FileText },
    ].filter(Boolean);

    // Email is platform_core too, and this branch returns before the shared nav
    // is assembled — so without an entry here a hospital tenant had no way to
    // reach campaigns, the sending pool, mailbox setup or templates, even with
    // every email feature enabled on its plan. Same omission WhatsApp had.
    const canEngageEmail = (feature) =>
      hasFeature(feature) && (isPermAdmin || hasPermission("email", "view"));

    const engageEmailSubItems = [
      canEngageEmail("email_campaigns")   && { label: "Campaigns",         href: "/email/campaigns",            icon: Send },
      canEngageEmail("email_campaigns")   && { label: "Sending Pool",      href: "/email/sending-pool",         icon: Server },
      canEngageEmail("email_integration") && { label: "Email Integration", href: "/settings/email-integration", icon: Mail },
      canEngageEmail("email_templates")   && { label: "Email Templates",   href: "/settings/email-templates",   icon: FileText },
    ].filter(Boolean);

    const engageAICallerSubItems = [
      { label: "Call Bucket", href: "/engage/ai-caller/bucket",   icon: Inbox },
      { label: "Call Logs",   href: "/engage/ai-caller/calls",    icon: ListOrdered },
      { label: "Campaigns",   href: "/engage/ai-caller/campaigns", icon: PhoneCall },
      { label: "Settings",    href: "/engage/ai-caller/settings", icon: Settings },
    ];

    // Fine-grained settings gating — super_admin sees everything; others only
    // see items whose module they have `view` permission for. If the user
    // has zero settings permissions, we hide the whole Settings group below.
    const engageSettingsSubItems = [
      (isPermAdmin || hasPermission("users", "view")) &&
        { label: "Users",              href: "/engage/settings/users",              icon: UserCog },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Auto Assignment",    href: "/engage/settings/assignment",         icon: Shuffle },
      isPermAdmin &&
        { label: "Roles",              href: "/settings/roles",                     icon: Shield },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Referral Sources",   href: "/engage/settings/sources",            icon: Handshake },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Procedure Catalog",  href: "/engage/settings/procedure-catalog",  icon: Wallet },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Department ARPPs",   href: "/engage/settings/department-arpp",    icon: Building2 },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Patient Types",      href: "/engage/settings/patient-types",      icon: Tag },
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "eMedHub Link",       href: "/engage/settings/emedhub",            icon: Link2 },
      // Lead capture. Both pages are product-neutral (the webhook files a hospital
      // lead as an enquiry) but this branch returns before the shared Settings
      // list is built, so they were reachable only by typing the URL.
      (isSuperAdmin || hasPermission("settings", "view")) &&
        { label: "Zapier / Webhook",   href: "/settings/zapier-webhook",            icon: Zap },
      (isSuperAdmin || hasPermission("meta_ads_settings", "view")) &&
        { label: "Meta Ads",           href: "/settings/meta-ads",                  icon: Megaphone },
      (isSuperAdmin || hasPermission("telephony_settings", "view")) &&
        { label: "Telephony",          href: "/settings/telephony",                 icon: PhoneCall },
      (isSuperAdmin || hasPermission("telephony_settings", "view")) &&
        { label: "Call Log",           href: "/telephony/calls",                    icon: PhoneIncoming },
    ].filter(Boolean);

    // Engage item is visible when the plan feature is ON *and* the role has the
    // page permission (admins/super_admins bypass the permission check).
    // Engage permission keys are the same string as their feature key.
    const canEngage = (key) => hasFeature(key) && (isPermAdmin || hasPermission(key, "view"));

    const engageNavItems = [
      canEngage("engage_dashboard")          && { label: "Dashboard",             href: "/engage/dashboard",          icon: LayoutDashboard },
      canEngage("engage_enquiries")          && { label: "Patient Enquiries",     href: "/engage/enquiries",          icon: Building },
      // Patients sit behind the enquiries permission (engage/permissions.py maps "patients" to it).
      canEngage("engage_enquiries")          && { label: "Patients",              href: "/engage/patients",           icon: UserCheck },
      canEngage("engage_referring_doctors")  && { label: "Referring Doctors",     href: "/engage/referring-doctors",  icon: BadgeCheck },
      canEngage("engage_ambulance")          && { label: "Ambulance Partners",    href: "/engage/ambulance-providers", icon: Handshake },
      canEngage("engage_corporate_accounts") && { label: "Corporate Accounts",    href: "/engage/corporate-accounts", icon: Briefcase },
      canEngage("engage_digital_marketing")  && { label: "Digital Marketing",     href: "/engage/digital-marketing",  icon: LineChart },
      canEngage("engage_followups")          && { label: "Follow-ups",            href: "/engage/followups",          icon: Tractor },
      canEngage("engage_bulk_import")        && { label: "Bulk Import",           href: "/engage/imports",            icon: Database },
      canEngage("engage_reports") && {
        label: "Reports", href: "/engage/reports", icon: BarChart3,
        isSecondary: true, subItems: engageReportsSubItems,
      },
      engageAdReportsSubItems.length > 0 && {
        label: "Ad Reports", href: engageAdReportsSubItems[0].href, icon: Megaphone,
        isSecondary: true, subItems: engageAdReportsSubItems,
      },
      canEngage("engage_ai_caller") && {
        label: "AI Caller", href: engageAICallerSubItems[0].href, icon: Phone,
        isSecondary: true, subItems: engageAICallerSubItems,
      },
      // WhatsApp is a platform_core app, not a TrustEdge one — same backend,
      // same single 'whatsapp' permission, for either product. This branch
      // returns before the shared nav is built, so without an entry here the
      // menu was unreachable for Engage tenants even though their plan enables
      // the whatsapp_* features and the /whatsapp/* routes are not product-gated.
      engageWhatsAppSubItems.length > 0 && {
        label: "WhatsApp", href: engageWhatsAppSubItems[0].href, icon: MessageSquare,
        isSecondary: true, subItems: engageWhatsAppSubItems,
      },
      engageEmailSubItems.length > 0 && {
        label: "Email", href: engageEmailSubItems[0].href, icon: Mail,
        isSecondary: true, subItems: engageEmailSubItems,
      },
      engageSettingsSubItems.length > 0 && {
        label: "Settings", href: engageSettingsSubItems[0].href, icon: Settings,
        isSecondary: true, subItems: engageSettingsSubItems,
      },
    ].filter(Boolean);

    return { navItems: engageNavItems };
  }

  const REPORT_MODULES = [
    "report_timeline", "report_pipeline_activity", "report_lead_sources", "report_lead_types",
    "report_lead_stages",
    "report_lead_potential", "report_sales_forecast", "report_engagement", "report_conversion",
    "report_email_analytics", "report_channel_partners",
  ];

  // Sales Manager Agent (sales_agent/). The Action Center itself stays under
  // Reports too, where it opens on the tracked alerts for these tenants.
  const salesAgentOn = hasFeature("sales_manager_agent");
  const salesAgentSubItems = [
    salesAgentOn && (isSuperAdmin || hasPermission("sales_agent", "view")) &&
      { label: "Overview", href: "/sales-agent", icon: LayoutDashboard },
    salesAgentOn && (isSuperAdmin || hasPermission("sales_agent", "view")) &&
      { label: "Action Center", href: "/reports/action-center", icon: Siren },
    salesAgentOn && (isSuperAdmin || hasPermission("sales_agent", "view")) &&
      { label: "Briefings", href: "/sales-agent/briefings", icon: Clock },
    salesAgentOn && (isSuperAdmin || hasPermission("sales_agent_settings", "view")) &&
      { label: "Settings", href: "/sales-agent/settings", icon: SlidersHorizontal },
  ].filter(Boolean);

  // Ordered by how a CRM team works through the day — keep in step with
  // constants/reportCatalog.js, which groups the Reports index the same way.
  const reportsSubItems = [
    // Timeline leads the list (asked for on 2026-10-13).
    can("report_timeline")        && { label: "Timeline",        href: "/reports/timeline",        icon: Clock },
    // Every report on one page — shown when the user can see at least one of them
    (REPORT_MODULES.some(can) || (hasFeature("customers") && hasPermission("onboarded", "view"))) &&
      { label: "All-in-One Report", href: "/reports/all", icon: Layers },
    can("report_timeline")        && { label: "Action Center",   href: "/reports/action-center",   icon: Siren },
    can("report_timeline")        && { label: "AI Next Actions", href: "/reports/ai-suggestions",  icon: Sparkles },
    can("report_timeline")        && { label: "Demo Desk",       href: "/reports/demo-desk",       icon: CalendarClock },
    can("report_engagement")      && { label: "Engagement",      href: "/reports/engagement",      icon: Activity },
    can("report_engagement")      && { label: "Lead Welcome",    href: "/reports/lead-welcome",    icon: HeartHandshake },
    can("report_engagement")      && { label: "Demo Report",     href: "/reports/demo-report",     icon: Presentation },
    can("report_engagement")      && { label: "Nurture",         href: "/reports/nurture",         icon: Leaf },
    hasFeature("customers") && (isSuperAdmin || hasPermission("report_pipeline_activity", "view")) &&
      { label: "Pipeline Activity", href: "/reports/pipeline-activity", icon: Gauge },
    can("report_sales_forecast")  && { label: "Sales Forecast",  href: "/reports/sales-forecast",  icon: TrendingUp },
    // Gated on `customers` rather than its own key, like Pipeline Activity above:
    // it reports on lead data every TrustEdge tenant already has, so it is not a
    // separately sold feature. hasFeature() returns false for any key absent from
    // the tenant's plan, so keying it to itself would hide the report from
    // everyone until each plan was edited.
    hasFeature("customers") && (isSuperAdmin || hasPermission("report_lead_stages", "view")) &&
      { label: "Lead Stages",       href: "/reports/lead-stages",       icon: ListChecks },
    can("report_conversion")      && { label: "Conversion",      href: "/reports/conversion",      icon: Target },
    can("report_lead_potential")  && { label: "Lead Potential",  href: "/reports/lead-potential",  icon: Flame },
    can("report_timeline")        && { label: "Team Performance", href: "/reports/team-performance", icon: Users },
    can("report_timeline")        && { label: "Coaching",        href: "/reports/coaching",        icon: GraduationCap },
    can("report_email_analytics") && { label: "Email Analytics", href: "/reports/email-analytics", icon: Mail },
    can("report_lead_sources")    && { label: "Meta Performance", href: "/reports/meta-performance", icon: Award },
    can("report_lead_types")      && { label: "Lead Source",     href: "/reports/lead-types",      icon: LineChart },
    can("report_lead_sources")    && { label: "Lead Meta Form",  href: "/reports/lead-sources",    icon: PieChart },
    // Gated on `customers`, not on its own key — same reason as Lead Stages
    // above: hasFeature() is false for any key absent from the tenant's plan, so
    // can("report_channel_partners") would hide this from everyone.
    hasFeature("customers") && (isSuperAdmin || hasPermission("report_channel_partners", "view")) &&
      { label: "Partner Sourcing", href: "/reports/channel-partners", icon: Handshake },
    hasFeature("customers") && (isSuperAdmin || hasPermission("report_assignment", "view") || hasPermission("lead_assignment", "view")) &&
      { label: "Lead Assignment", href: "/reports/lead-assignment", icon: Shuffle },
    hasFeature("customers") && hasPermission("onboarded", "view") &&
      { label: "Implementation Status", href: "/onboarded/reports/implementation", icon: ListChecks },
    hasFeature("customers") && hasPermission("onboarded", "view") &&
      { label: "AMC Tracker",           href: "/onboarded/reports/amc-tracker",    icon: CalendarClock },
    hasFeature("customers") && hasPermission("onboarded", "view") &&
      { label: "Onboarded Revenue",     href: "/onboarded/reports/revenue",        icon: IndianRupee },
    hasFeature("customers") && hasPermission("onboarded", "view") &&
      { label: "Onboarded Clients",     href: "/onboarded/reports/client-list",    icon: Building2 },
    (isSuperAdmin || hasPermission("reports", "view")) && { label: "Audit Log", href: "/reports/audit-log", icon: ClipboardList },
  ].filter(Boolean);

  // Meta Ads gets its own menu rather than twelve more rows under Reports.
  // Appended there, the list ran to 32 entries and anything in the middle was
  // unreachable without scrolling — "Funnel by Ad" sat at number 25.
  //
  // Not can(): that helper assumes the plan-feature key equals the permission
  // key, which holds for the reports above but not here — every ad page is
  // gated by the single 'meta_ads' feature (see permission_registry), so
  // hasFeature('report_ad_performance') would never be true and these links
  // would stay hidden however the role is configured.
  const adReportsSubItems = [
    canAd("report_ad_performance") && { label: "Ad Performance",     href: "/reports/ad-performance",       icon: Megaphone },
    canAd("report_ad_dashboard") && { label: "Live Ad Spend",      href: "/reports/ad-dashboard",         icon: Radio },
    canAd("report_ad_roi") && { label: "Marketing ROI",      href: "/reports/ad-roi",               icon: IndianRupee },
    canAd("report_ad_lead_quality") && { label: "Lead Quality by Ad", href: "/reports/ad-lead-quality",      icon: Award },
    canAd("report_ad_funnel") && { label: "Funnel by Ad",       href: "/reports/ad-funnel",            icon: Filter },
    canAd("report_ad_attribution") && { label: "Lead Attribution",   href: "/reports/ad-attribution",       icon: GitBranch },
    canAd("report_ad_creative") && { label: "Ad Creative",        href: "/reports/ad-creative",          icon: ImageIcon },
    canAd("report_ad_pacing") && { label: "Spend Pacing",       href: "/reports/ad-pacing",            icon: Gauge },
    canAd("report_ad_audience") && { label: "Ad Audience",        href: "/reports/ad-audience",          icon: Users },
    canAd("report_ad_geography") && { label: "Ad Geography",       href: "/reports/ad-geography",         icon: MapPin },
    canAd("report_ad_rep") && { label: "Rep × Ad",           href: "/reports/ad-rep-effectiveness", icon: UserCheck },
    canAd("report_ad_time_lag") && { label: "Time to Revenue",    href: "/reports/ad-time-lag",          icon: Timer },
    canAd("report_ad_conversions") && { label: "Conversions to Meta", href: "/reports/ad-conversions",  icon: Upload },
  ].filter(Boolean);

  // WhatsApp is gated by a per-sub-page PLAN FEATURE but a single PERMISSION:
  // every endpoint in platform_core/whatsapp enforces the one 'whatsapp' key
  // (22 call sites), which is also what the /whatsapp/* route guards in App.js
  // check. Asking for permission 'whatsapp_campaigns' here meant the sidebar
  // stayed empty for any non-super_admin no matter what the Roles editor granted.
  const canWhatsApp = (feature) =>
    hasFeature(feature) && (isSuperAdmin || hasPermission("whatsapp", "view"));

  const whatsappSubItems = [
    canWhatsApp("whatsapp_campaigns")   && { label: "Campaigns",      href: "/whatsapp/campaigns",            icon: Send },
    canWhatsApp("whatsapp_inbox")       && { label: "Inbox",          href: "/whatsapp/inbox",                icon: Inbox },
    canWhatsApp("whatsapp_integration") && { label: "WA Integration", href: "/settings/whatsapp-integration", icon: MessageSquare },
    canWhatsApp("whatsapp_templates")   && { label: "WA Templates",   href: "/settings/whatsapp-templates",   icon: FileText },
  ].filter(Boolean);

  const aiCallerSubItems = [
    can("ai_caller_dashboard")  && { label: "Dashboard",  href: "/ai-caller",           icon: LayoutDashboard },
    can("ai_caller_campaigns")  && { label: "Call Bucket", href: "/ai-caller/bucket",    icon: Inbox },
    can("ai_caller_dashboard")  && { label: "Call Logs",   href: "/ai-caller/calls",     icon: ListOrdered },
    can("ai_caller_campaigns")  && { label: "Campaigns",   href: "/ai-caller/campaigns", icon: PhoneCall },
    can("ai_caller_settings")   && { label: "Settings",    href: "/settings/ai-caller",  icon: Settings },
  ].filter(Boolean);

  // Same split as WhatsApp: per-sub-page plan FEATURE, single 'email'
  // PERMISSION — every endpoint in platform_core/email_integration enforces
  // that one key, and it is what the /email/* route guards check.
  const canEmail = (feature) =>
    hasFeature(feature) && (isSuperAdmin || hasPermission("email", "view"));

  // All four use canEmail, not can(). `can()` asks for a permission named after
  // the feature — 'email_integration', 'email_templates' — but no such
  // permission exists: the backend enforces a single 'email' key, and that is
  // the only one in ALL_PERMISSION_MODULES. So these two were invisible to
  // every non-super_admin no matter what the plan or the Roles editor said.
  const emailSubItems = [
    canEmail("email_campaigns")  && { label: "Campaigns",         href: "/email/campaigns",            icon: Send },
    canEmail("email_campaigns")  && { label: "Sending Pool",      href: "/email/sending-pool",         icon: Server },
    canEmail("email_integration") && { label: "Email Integration", href: "/settings/email-integration", icon: Mail },
    canEmail("email_templates")   && { label: "Email Templates",   href: "/settings/email-templates",   icon: FileText },
  ].filter(Boolean);

  // Cold outbound (trustedge prospecting/). One 'outbound' permission for the
  // work pages, as the backend enforces; its settings page is under Settings.
  const outboundSubItems = hasFeature("customers") && (isSuperAdmin || hasPermission("outbound", "view")) ? [
    { label: "Overview",  href: "/outbound",           icon: LayoutDashboard },
    { label: "Prospects", href: "/outbound/prospects", icon: Telescope },
    { label: "Sequences", href: "/outbound/sequences", icon: Send },
    { label: "Outbox",    href: "/outbound/outbox",    icon: Inbox },
    { label: "Replies",   href: "/outbound/replies",   icon: MailCheck },
  ] : [];

  // Administering people is not a system setting. Both used to sit under
  // Settings, which meant an admin with no 'settings' permission still saw a
  // Settings menu — the two items they are always entitled to made the whole
  // menu appear, and it read as a permission leak. Separated so each menu means
  // one thing: Administration is always open to an admin, Settings is gated.
  const adminSubItems = [
    // Driven by the granted permission, not by being an admin. 'admin' used to
    // see both unconditionally, so the menu appeared for administrators who had
    // never been given it. An admin can still have these — by ticking 'users' on
    // their role, which is the point of the Roles screen.
    (isSuperAdmin || hasPermission("users", "view")) &&
      { label: "Users", href: "/settings/users", icon: UserCog },
    // Roles stays super_admin only. It is the screen that grants every other
    // permission, so anyone who can edit it can escalate themselves to anything.
    isSuperAdmin &&
      { label: "Roles", href: "/settings/roles", icon: Shield },
    // Sending a release note is administering people, not configuring the
    // product, so it sits here rather than under Settings.
    (isSuperAdmin || hasPermission("releases", "view")) &&
      { label: "Release Notes", href: "/settings/releases", icon: Megaphone },
  ].filter(Boolean);

  const settingsSubItems = [
    isSuperAdmin &&
      { label: "Customer Types",  href: "/settings/customer-types", icon: Tag },
    isSuperAdmin &&
      { label: "Custom Fields",   href: "/settings/custom-fields",  icon: Sliders },
    // Visible to anyone with onboarded access: an approver who is not a super
    // admin still needs to find the page to see who else can sign off.
    { label: "Implementation Approvals", href: "/settings/implementation-approvals", icon: ShieldCheck },
    /* Visible to anyone with MOU access, not just super admins: the page is
       read-only for everyone else and shows what gate they are working under,
       which is more useful than hiding it. */
    hasPermission("mou", "view") &&
      { label: "MOU", href: "/settings/mou", icon: FileSignature },
    isSuperAdmin &&
      { label: "Modules",         href: "/settings/modules",        icon: Database },
    isSuperAdmin &&
      { label: "Quotation Template", href: "/settings/quotation-template", icon: FileText },
    isSuperAdmin &&
      { label: "Tier Pricing",       href: "/settings/product-pricing",    icon: Calculator },
    isSuperAdmin &&
      { label: "Plan Services",      href: "/settings/plan-services",      icon: Database },
    isSuperAdmin &&
      { label: "Leak Assumptions",   href: "/settings/leak-assumptions",   icon: Calculator },
    isSuperAdmin &&
      { label: "Projects",        href: "/settings/projects",       icon: Briefcase },
    isSuperAdmin &&
      { label: "Hospital Profile", href: "/settings/hospital-profile", icon: Building },
    isSuperAdmin &&
      { label: "CRE Handover",   href: "/settings/cre-handover",   icon: ClipboardList },
    isSuperAdmin &&
      { label: "Mandatory Fields", href: "/settings/mandatory-fields", icon: ListChecks },
    isSuperAdmin &&
      { label: "Duplicate Rules", href: "/settings/duplicate-rules", icon: Copy },
    isSuperAdmin &&
      { label: "Lead Priority",  href: "/settings/lead-priority",  icon: Flame },
    // Who gets new leads - teams, limits, rules (customers/assignment/).
    hasFeature("customers") && (isSuperAdmin || hasPermission("lead_assignment", "view")) &&
      { label: "Lead Assignment", href: "/settings/lead-assignment", icon: Shuffle },
    // Grantable on its own (Settings → Roles → City → State), so a sales
    // manager can map cities without being a super admin.
    (isSuperAdmin || hasPermission("city_state_map", "view")) &&
      { label: "City → State",   href: "/settings/city-state",     icon: MapPin },
    // The research agent on lead pages (research/): depth, limits, ideal customer.
    hasFeature("company_research") && (isSuperAdmin || hasPermission("company_research_settings", "view")) &&
      { label: "Company Research", href: "/settings/company-research", icon: Search },
    (isSuperAdmin || hasPermission("settings", "view")) &&
      { label: "Forecast Config", href: "/settings/forecast-config", icon: SlidersHorizontal },
    (isSuperAdmin || hasPermission("settings", "view")) &&
      { label: "Zapier / Webhook", href: "/settings/zapier-webhook", icon: Zap },
    (isSuperAdmin || hasPermission("settings", "view")) &&
      { label: "ERP Integration", href: "/settings/erp-integration", icon: Database },
    (isSuperAdmin || hasPermission("settings", "view")) &&
      { label: "Referral Programme", href: "/settings/referral-program", icon: Gift },
    (isSuperAdmin || hasPermission("settings", "view")) &&
      { label: "Referral Sync", href: "/settings/referral-sync", icon: RefreshCw },
    (isSuperAdmin || hasPermission("meta_ads_settings", "view")) &&
      { label: "Meta Ads", href: "/settings/meta-ads", icon: Megaphone },
    (isSuperAdmin || hasPermission("telephony_settings", "view")) &&
      { label: "Telephony", href: "/settings/telephony", icon: PhoneCall },
    (isSuperAdmin || hasPermission("telephony_settings", "view")) &&
      { label: "Call Log", href: "/telephony/calls", icon: PhoneIncoming },
    hasFeature("customers") && (isSuperAdmin || hasPermission("outbound_settings", "view")) &&
      { label: "Outbound", href: "/settings/outbound", icon: Radar },
  ].filter(Boolean);

  const navItems = [
    /* Always shown. The dashboard reports only what the signed-in user can
       already see — it is scoped by the same rule as the leads list — so gating
       it per role hid a safe page from 11 of 18 users in the live tenant, two of
       whom had no landing page with any numbers on it at all. */
    { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },

    // excludePrefixes: the Unassigned queue lives under /customers but has its
    // own menu entry, and both lit up at once.
    hasFeature("customers") && hasPermission("customers", "view") &&
      { label: "Customers", href: "/customers", icon: Building, excludePrefixes: ["/customers/unassigned"] },

    // The lead assignment queue. Managers who can move leads; an admin who can
    // edit customers counts, matching the backend's can_reassign.
    hasFeature("customers") && (isSuperAdmin || hasPermission("lead_reassign", "view")
        || (login_role === "admin" && hasPermission("customers", "edit"))) &&
      { label: "Unassigned Leads", href: "/customers/unassigned", icon: Inbox },

    hasFeature("customers") && hasPermission("channel_partners", "view") &&
      { label: "Channel Partners", href: "/channel-partners", icon: Handshake },

    // Rewards for referrals that converted. Beside Customers rather than under
    // Settings: the rates are policy, but awarding is about a customer.
    hasFeature("customers") && hasPermission("customers", "view") &&
      { label: "Referral Rewards", href: "/referral-rewards", icon: Gift },

    hasFeature("customers") && (isSuperAdmin || hasPermission("quotations", "view")) &&
      { label: "Quotations", href: "/quotations", icon: FileText },

    /* The stage between a won deal and an onboarded client. Listed after
       Quotations and before Onboarded Clients because that is the order the
       work happens in, and a sidebar that lists them out of order teaches the
       wrong sequence to anyone new. */
    hasFeature("customers") && hasPermission("mou", "view") &&
      { label: "MOU", href: "/mou", icon: FileSignature },

    hasFeature("customers") && hasPermission("onboarded", "view") &&
      { label: "Onboarded Clients", href: "/onboarded", icon: BadgeCheck, excludePrefixes: ["/onboarded/reports/"] },

    hasFeature("followup") && hasPermission("followup", "view") &&
      { label: "Follow up", href: "/followup", icon: Tractor },

    hasFeature("tasks") && hasPermission("tasks", "view") &&
      { label: "Tasks", href: "/tasks", icon: CheckSquare },

    hasFeature("followup") && hasPermission("followup", "view") &&
      { label: "Demos", href: "/demos/calendar", icon: CalendarClock },

    salesAgentSubItems.length > 0 && {
      label: "Sales Intelligence",
      href: salesAgentSubItems[0].href,
      icon: Sparkles,
      isSecondary: true,
      subItems: salesAgentSubItems,
    },

    reportsSubItems.length > 0 && {
      label: "Reports",
      href: reportsSubItems[0].href,
      icon: BarChart3,
      isSecondary: true,
      subItems: reportsSubItems,
    },

    adReportsSubItems.length > 0 && {
      label: "Ad Reports",
      href: adReportsSubItems[0].href,
      icon: Megaphone,
      isSecondary: true,
      subItems: adReportsSubItems,
    },

    whatsappSubItems.length > 0 && {
      label: "WhatsApp",
      href: whatsappSubItems[0].href,
      icon: MessageSquare,
      isSecondary: true,
      subItems: whatsappSubItems,
    },

    aiCallerSubItems.length > 0 && {
      label: "AI Caller",
      href: aiCallerSubItems[0].href,
      icon: Phone,
      isSecondary: true,
      subItems: aiCallerSubItems,
    },

    emailSubItems.length > 0 && {
      label: "Email",
      href: emailSubItems[0].href,
      icon: Mail,
      isSecondary: true,
      subItems: emailSubItems,
    },

    outboundSubItems.length > 0 && {
      label: "Outbound",
      href: outboundSubItems[0].href,
      icon: Radar,
      isSecondary: true,
      subItems: outboundSubItems,
    },

    adminSubItems.length > 0 && {
      label: "Administration",
      href: adminSubItems[0].href,
      icon: UserCog,
      isSecondary: true,
      subItems: adminSubItems,
    },

    settingsSubItems.length > 0 && {
      label: "Settings",
      // The parent used to point at /settings, which has no route — clicking the
      // label went nowhere. Point it at the first child that is actually visible.
      href: settingsSubItems[0].href,
      icon: Settings,
      isSecondary: true,
      subItems: settingsSubItems,
    },

  ].filter(Boolean);

  return { navItems };
};
